import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useUpdatedRef from '../../../utils/react/useUpdatedRef';
import { deselectElementChip, selectElementChip } from '../elementChipsActions';
import { selectSelectedElementChip } from '../elementChipsSelectors';
import type { InteractiveElementChipDescriptor } from '../elementChipsTypes';

type UseElementChipSelectionArgs = {
    chips: InteractiveElementChipDescriptor[];
    elementId: string;
    selectedElementId: string;
    isElementSingleSelected: boolean;
    clearEditing: () => void;
};

type UseElementChipSelectionResult = {
    chips: InteractiveElementChipDescriptor[];
    deselectChip: () => void;
};

/**
 * Owns global chip selection for the current element chip container.
 */
export const useElementChipSelection = ({
    chips,
    elementId,
    selectedElementId,
    isElementSingleSelected,
    clearEditing,
}: UseElementChipSelectionArgs): UseElementChipSelectionResult => {
    const dispatch = useDispatch();
    const selectedChip = useSelector(selectSelectedElementChip);
    const ownsSelectedChip =
        selectedChip?.elementId === elementId && selectedChip.selectedElementId === selectedElementId;
    const selectedChipId = ownsSelectedChip ? selectedChip?.chip.id : undefined;
    const ownsSelectedChipRef = useUpdatedRef(ownsSelectedChip);

    const deselectChip = useCallback(() => {
        dispatch(deselectElementChip());
        clearEditing();
    }, [clearEditing, dispatch]);

    useEffect(() => {
        if (!ownsSelectedChip) return;
        if (isElementSingleSelected && chips.some((chip) => chip.id === selectedChipId)) return;

        deselectChip();
    }, [deselectChip, isElementSingleSelected, ownsSelectedChip, chips, selectedChipId]);

    useEffect(() => {
        return () => {
            if (!ownsSelectedChipRef.current) return;

            deselectChip();
        };
    }, [dispatch, deselectChip]);

    const selectedChips = useMemo(
        () =>
            chips.map((chip) => {
                const isSelected = chip.activationMode === 'select' && chip.id === selectedChipId;
                const canActivate =
                    chip.interaction.canModify && (chip.activationMode === 'immediate' || isElementSingleSelected);
                const { interaction: _interaction, ...selectedDescriptor } = chip;

                return {
                    ...chip,
                    interaction: {
                        ...chip.interaction,
                        isSelected,
                        canActivate,
                        onSelect: () => {
                            if (chip.activationMode !== 'select') return;
                            if (!canActivate) return;

                            dispatch(selectElementChip({ elementId, selectedElementId, chip: selectedDescriptor }));
                        },
                        onDeselect: deselectChip,
                    },
                };
            }),
        [deselectChip, dispatch, elementId, isElementSingleSelected, chips, selectedElementId, selectedChipId],
    );

    return {
        chips: selectedChips,
        deselectChip: deselectChip,
    };
};
